// src/pages/SettingsPage.js
import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
  Collapse,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  saveSettingsReplace,
  getSettingsByCode,
} from "../../services/Settings";

// Icons
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { getNotificationTypes } from "../../services/Notification";
import { getUserRoles } from "../../services/User";

// Role gate (placeholder)
const RoleBasedRender = ({ children /*, allowedRoles*/ }) => <>{children}</>;

/** -----------------------------
 * Helpers
 * ----------------------------- */
const toLabelFromEnumish = (s) => {
  if (!s || typeof s !== "string") return s ?? "";
  return s
    .toLowerCase()
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
};

const normalizeOptions = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  if (typeof arr[0] === "string") {
    return arr.map((v) => ({ value: v, label: toLabelFromEnumish(v) }));
  }
  return arr.map((o) => ({
    value: o.value ?? o.code ?? o.id ?? "",
    label:
      o.label ??
      o.name ??
      (typeof o.value === "string" ? toLabelFromEnumish(o.value) : ""),
  }));
};

/** -----------------------------
 * Styled UI bits
 * ----------------------------- */
const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
  minWidth: 120,
}));

const SectionCard = styled(Card)(({ theme }) => ({
  elevation: 1,
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(2),
  transition: "all 0.3s ease",
  "&:hover": {
    elevation: 3,
    transform: "translateY(-2px)",
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  cursor: "pointer",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
}));

/** -----------------------------
 * Settings config
 * ----------------------------- */
const buildSettingsConfig = () => [
  {
    id: "saleBill",
    title: "Sale Bill",
    icon: <ReceiptIcon />,
    subsections: [
      {
        id: "autoIncrementing",
        label: "Auto Incrementing",
        type: "checkbox",
        defaultValue: true,
        description: "Automatically increment bill numbers for new sales",
      },
      {
        id: "nextBillNumber",
        label: "Next Bill Number",
        type: "number",
        defaultValue: 1001,
        description: "The next bill number to be used",
        validation: { min: 1, max: 999999999999, required: true },
      },
    ],
  },
  {
    id: "notification",
    title: "Notification",
    icon: <NotificationsIcon />,
    subsections: [
      {
        id: "enableNotifications",
        label: "Enable Notifications (Global)",
        type: "checkbox",
        defaultValue: true,
        description: "Globally enable or disable all notifications",
      },
      // NOTE: the notification role matrix is NOT a simple subsection field.
      // We render a custom block for it and save it under notification.data.roleMatrix.
    ],
  },
  {
    id: "customer",
    title: "Customer",
    icon: <PersonIcon />,
    subsections: [
      {
        id: "customerCodeFormat",
        label: "Customer Code Format",
        type: "text",
        defaultValue: "CUST-",
        description: "Prefix for customer codes (e.g., CUST-001)",
        validation: { maxLength: 10 },
      },
      {
        id: "requirePhoneNumber",
        label: "Require Phone Number",
        type: "checkbox",
        defaultValue: false,
        description: "Make phone number mandatory for new customers",
      },
      {
        id: "requireAddress",
        label: "Require Address",
        type: "checkbox",
        defaultValue: false,
        description: "Make address mandatory for new customers",
      },
    ],
  },
  {
    id: "user",
    title: "User",
    icon: <AccountCircleIcon />,
    subsections: [
      {
        id: "sessionTimeout",
        label: "Session Timeout (minutes)",
        type: "number",
        defaultValue: 30,
        description: "User session timeout in minutes",
        validation: { min: 5, max: 480, required: true },
      },
      {
        id: "passwordMinLength",
        label: "Minimum Password Length",
        type: "number",
        defaultValue: 8,
        description: "Minimum required password length",
        validation: { min: 4, max: 32, required: true },
      },
      {
        id: "enableTwoFactor",
        label: "Enable Two-Factor Authentication",
        type: "checkbox",
        defaultValue: false,
        description: "Require two-factor authentication for all users",
      },
    ],
  },
];

function SettingsPage() {
  const theme = useTheme();
  const history = useHistory();

  const [settings, setSettings] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // dynamic options
  const [roleOptions, setRoleOptions] = useState([]);
  const [notificationOptions, setNotificationOptions] = useState([]);

  // notification role matrix persisted under "notification" settings
  // shape: { [type]: { enabled: boolean, roles: string[] } }
  const [notificationRoleMatrix, setNotificationRoleMatrix] = useState({});

  const settingsConfig = useMemo(() => buildSettingsConfig(), []);

  const buildDefaultMatrix = (notifOpts, roleOpts) => {
    const defaultRole =
      roleOpts.find((r) => r.value === "ROLE_ADMIN")?.value ||
      roleOpts[0]?.value ||
      "ROLE_ADMIN";
    const obj = {};
    notifOpts.forEach((n) => {
      obj[n.value] = { enabled: true, roles: [defaultRole] };
    });
    return obj;
  };

  useEffect(() => {
    const loadAll = async () => {
      try {
        // Expand all sections initially (and matrix block)
        const initialExpanded = {};
        settingsConfig.forEach(
          (section) => (initialExpanded[section.id] = true)
        );
        initialExpanded["notificationMatrix"] = true;
        setExpandedSections(initialExpanded);

        // 1) fetch dynamic reference data
        const [rolesRaw, notificationsRaw] = await Promise.all([
          getUserRoles(), // -> [{ roleId, roleName }, ...]
          getNotificationTypes(), // -> ["EMAIL","SMS",...]
        ]);

        // Flatten role objects -> ["ROLE_ADMIN","WT_BILL_CREATE", ...]
        const rolesList = (Array.isArray(rolesRaw) ? rolesRaw : [])
          .map((r) => r?.roleName)
          .filter(Boolean);

        // Deduplicate while keeping order
        const uniqueRolesList = [...new Set(rolesList)];

        // Normalize to { value, label }[]
        const normalizedRoles = normalizeOptions(uniqueRolesList);
        const normalizedNotifications = normalizeOptions(
          Array.isArray(notificationsRaw) ? notificationsRaw : []
        );

        setRoleOptions(normalizedRoles);
        setNotificationOptions(normalizedNotifications);

        // 2) load all settings sections (single mechanism)
        const mergedFromServer = {};
        const serverByCode = {}; // keep entire objects for special fields like roleMatrix

        await Promise.all(
          settingsConfig.map(async (section) => {
            const server = await getSettingsByCode(section.id);
            const serverMap =
              server && server.data && typeof server.data === "object"
                ? server.data
                : {};
            serverByCode[section.id] = serverMap;

            section.subsections.forEach((sub) => {
              mergedFromServer[sub.id] =
                serverMap[sub.id] !== undefined
                  ? serverMap[sub.id]
                  : sub.defaultValue;
            });
          })
        );

        // 3) extract notification role matrix from the "notification" settings
        const matrixFromServer = serverByCode["notification"]?.roleMatrix;
        const finalMatrix =
          matrixFromServer && typeof matrixFromServer === "object"
            ? // validate roles & types
              normalizedNotifications.reduce((acc, n) => {
                const existing = matrixFromServer[n.value];
                const validRoles = new Set(normalizedRoles.map((r) => r.value));
                if (
                  existing &&
                  typeof existing.enabled === "boolean" &&
                  Array.isArray(existing.roles)
                ) {
                  acc[n.value] = {
                    enabled: !!existing.enabled,
                    roles: (existing.roles || []).filter((r) =>
                      validRoles.has(r)
                    ),
                  };
                  if (acc[n.value].roles.length === 0) {
                    acc[n.value].roles = [
                      normalizedRoles[0]?.value || "ROLE_ADMIN",
                    ];
                  }
                } else {
                  acc[n.value] = {
                    enabled: true,
                    roles: [normalizedRoles[0]?.value || "ROLE_ADMIN"],
                  };
                }
                return acc;
              }, {})
            : buildDefaultMatrix(normalizedNotifications, normalizedRoles);

        setNotificationRoleMatrix(finalMatrix);
        setSettings(mergedFromServer);
      } catch (e) {
        console.error("Load error:", e);

        // fallback to defaults
        const fallbacks = {};
        settingsConfig.forEach((section) =>
          section.subsections.forEach(
            (sub) => (fallbacks[sub.id] = sub.defaultValue)
          )
        );
        setSettings(fallbacks);

        // fallback matrix uses same getters
        try {
          const [rolesRaw, notificationsRaw] = await Promise.all([
            getUserRoles(),
            getNotificationTypes(),
          ]);
          const rolesList = (Array.isArray(rolesRaw) ? rolesRaw : [])
            .map((r) => r?.roleName)
            .filter(Boolean);
          const uniqueRolesList = [...new Set(rolesList)];
          const normalizedRoles = normalizeOptions(uniqueRolesList);
          const normalizedNotifications = normalizeOptions(
            Array.isArray(notificationsRaw) ? notificationsRaw : []
          );
          setNotificationRoleMatrix(
            buildDefaultMatrix(normalizedNotifications, normalizedRoles)
          );
        } catch {
          // if even reference data fails, leave matrix as empty/default
          setNotificationRoleMatrix({});
        }

        setSnackbar({
          open: true,
          message: "Failed to load some settings. Showing defaults.",
          severity: "warning",
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSettingChange = (settingId, value) => {
    setSettings((prev) => ({ ...prev, [settingId]: value }));
    setHasChanges(true);
  };

  // matrix handlers
  const handleToggleNotificationEnabled = (notifValue, enabled) => {
    setNotificationRoleMatrix((prev) => ({
      ...prev,
      [notifValue]: {
        ...(prev[notifValue] || { enabled: true, roles: [] }),
        enabled,
      },
    }));
    setHasChanges(true);
  };

  const handleNotificationRolesChange = (notifValue, rolesArray) => {
    setNotificationRoleMatrix((prev) => ({
      ...prev,
      [notifValue]: {
        ...(prev[notifValue] || { enabled: true, roles: [] }),
        roles: rolesArray,
      },
    }));
    setHasChanges(true);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Build per-section payloads from flat `settings`
      const perSection = {};
      settingsConfig.forEach((section) => {
        perSection[section.id] = {};
        section.subsections.forEach((sub) => {
          perSection[section.id][sub.id] = settings[sub.id];
        });
      });

      // Inject the roleMatrix inside the "notification" section payload
      perSection["notification"] = {
        ...(perSection["notification"] || {}),
        roleMatrix: notificationRoleMatrix,
      };

      // Save all sections via the SAME method
      const results = await Promise.all(
        Object.entries(perSection).map(([code, data]) =>
          saveSettingsReplace(code, data)
        )
      );
      const allOk = results.every(Boolean);
      if (!allOk) throw new Error("One or more sections failed to save");

      setHasChanges(false);
      setSnackbar({
        open: true,
        message: "All settings saved successfully!",
        severity: "success",
      });
    } catch (error) {
      console.error("Save error:", error);
      setSnackbar({
        open: true,
        message: "Failed to save settings. Please try again.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    const resetSettings = {};
    settingsConfig.forEach((section) => {
      section.subsections.forEach((subsection) => {
        resetSettings[subsection.id] = subsection.defaultValue;
      });
    });
    setSettings(resetSettings);

    // also reset matrix
    setNotificationRoleMatrix(
      buildDefaultMatrix(notificationOptions, roleOptions)
    );

    setHasChanges(true);
    setSnackbar({
      open: true,
      message: "Settings reset to default values",
      severity: "info",
    });
  };

  const renderSubsection = (subsection) => {
    const value = settings[subsection.id];

    switch (subsection.type) {
      case "checkbox":
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) =>
                  handleSettingChange(subsection.id, e.target.checked)
                }
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="500">
                  {subsection.label}
                </Typography>
                {subsection.description && (
                  <Typography variant="caption" color="text.secondary">
                    {subsection.description}
                  </Typography>
                )}
              </Box>
            }
            sx={{ mb: 2, alignItems: "flex-start" }}
          />
        );

      case "select":
        return (
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{subsection.label}</InputLabel>
              <Select
                value={value ?? subsection.defaultValue}
                label={subsection.label}
                onChange={(e) =>
                  handleSettingChange(subsection.id, e.target.value)
                }
                sx={{ borderRadius: 2 }}
              >
                {(subsection.options || []).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {subsection.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {subsection.description}
              </Typography>
            )}
          </Box>
        );

      case "multiselect":
        return (
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{subsection.label}</InputLabel>
              <Select
                multiple
                value={value ?? subsection.defaultValue ?? []}
                label={subsection.label}
                onChange={(e) =>
                  handleSettingChange(subsection.id, e.target.value)
                }
                input={<OutlinedInput label={subsection.label} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((val) => {
                      const option = (subsection.options || []).find(
                        (opt) => opt.value === val
                      );
                      return (
                        <Chip
                          key={val}
                          label={option?.label || toLabelFromEnumish(val)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                )}
                sx={{ borderRadius: 2 }}
              >
                {(subsection.options || []).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      checked={
                        (value ?? subsection.defaultValue ?? []).indexOf(
                          option.value
                        ) > -1
                      }
                    />
                    <Typography>{option.label}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {subsection.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {subsection.description}
              </Typography>
            )}
          </Box>
        );

      case "number":
        return (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              type="text" // free typing; clamp on blur
              inputMode="numeric"
              label={subsection.label}
              value={value ?? ""}
              onChange={(e) =>
                handleSettingChange(subsection.id, e.target.value)
              }
              onBlur={(e) => {
                const raw = String(e.target.value ?? "").trim();
                if (raw === "") {
                  handleSettingChange(subsection.id, "");
                  return;
                }
                const min =
                  subsection.validation?.min ?? Number.MIN_SAFE_INTEGER;
                const max =
                  subsection.validation?.max ?? Number.MAX_SAFE_INTEGER;
                const n = Number(raw);
                const clamped = Number.isFinite(n)
                  ? Math.max(min, Math.min(max, Math.trunc(n)))
                  : min;
                handleSettingChange(subsection.id, clamped);
              }}
              inputProps={{
                min: subsection.validation?.min,
                max: subsection.validation?.max,
                pattern: "[0-9]*",
              }}
              required={subsection.validation?.required}
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            {subsection.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {subsection.description}
              </Typography>
            )}
          </Box>
        );

      case "text":
        return (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label={subsection.label}
              value={value ?? ""}
              onChange={(e) =>
                handleSettingChange(subsection.id, e.target.value)
              }
              inputProps={{ maxLength: subsection.validation?.maxLength }}
              size="small"
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            {subsection.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {subsection.description}
              </Typography>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const renderNotificationMatrix = () => {
    if (!notificationOptions.length) return null;

    return (
      <SectionCard>
        <SectionHeader onClick={() => toggleSection("notificationMatrix")}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <NotificationsIcon sx={{ color: "primary.main" }} />
            <Typography variant="h6" fontWeight="600" color="primary.main">
              Notification Access Matrix
            </Typography>
          </Box>
          {expandedSections["notificationMatrix"] ? (
            <ExpandLessIcon color="primary" />
          ) : (
            <ExpandMoreIcon color="primary" />
          )}
        </SectionHeader>

        <Collapse
          in={expandedSections["notificationMatrix"] ?? true}
          timeout="auto"
          unmountOnExit
        >
          <CardContent sx={{ pt: 3 }}>
            <Grid container spacing={2}>
              {notificationOptions.map((n) => {
                const row = notificationRoleMatrix[n.value] || {
                  enabled: true,
                  roles: [],
                };
                return (
                  <Grid item xs={12} key={n.value}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 2,
                        p: 2,
                        border: `1px solid ${alpha(
                          theme.palette.divider,
                          0.5
                        )}`,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.015),
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!row.enabled}
                              onChange={(e) =>
                                handleToggleNotificationEnabled(
                                  n.value,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label={
                            <Typography variant="subtitle1" fontWeight={600}>
                              {n.label}
                            </Typography>
                          }
                        />
                      </Box>

                      <Box>
                        <FormControl fullWidth size="small">
                          <InputLabel>Visible Roles</InputLabel>
                          <Select
                            multiple
                            value={row.roles || []}
                            onChange={(e) =>
                              handleNotificationRolesChange(
                                n.value,
                                e.target.value
                              )
                            }
                            input={<OutlinedInput label="Visible Roles" />}
                            renderValue={(selected) => (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                }}
                              >
                                {selected.map((val) => {
                                  const option = roleOptions.find(
                                    (opt) => opt.value === val
                                  );
                                  return (
                                    <Chip
                                      key={val}
                                      label={
                                        option?.label || toLabelFromEnumish(val)
                                      }
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {roleOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                <Checkbox
                                  checked={(row.roles || []).includes(
                                    option.value
                                  )}
                                />
                                <Typography>{option.label}</Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Collapse>
      </SectionCard>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        m: { xs: 1, md: 3 },
        borderRadius: 2,
        bgcolor: "#f9fafb",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <SettingsIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Settings
          </Typography>
        </Box>
        <IconButton
          onClick={() => history.goBack()}
          aria-label="close"
          sx={{
            bgcolor: alpha(theme.palette.grey[500], 0.1),
            "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.2) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Action Buttons */}
      <RoleBasedRender
        allowedRoles={["VIEW_SETTINGS", "EDIT_SETTINGS", "ROLE_ADMIN"]}
      >
        <Box sx={{ mb: 4, display: "flex", justifyContent: "flex-end" }}>
          <Stack direction="row" spacing={2}>
            <ActionButton
              variant="outlined"
              color="secondary"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              disabled={isSaving}
            >
              Reset to Defaults
            </ActionButton>
            <ActionButton
              variant="contained"
              color="primary"
              startIcon={
                isSaving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </ActionButton>
          </Stack>
        </Box>
      </RoleBasedRender>

      {/* Settings Sections */}
      <Grid container spacing={3}>
        {settingsConfig.map((section) => (
          <Grid item xs={12} key={section.id}>
            <SectionCard>
              <SectionHeader onClick={() => toggleSection(section.id)}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {React.cloneElement(section.icon, {
                    sx: { color: "primary.main" },
                  })}
                  <Typography
                    variant="h6"
                    fontWeight="600"
                    color="primary.main"
                  >
                    {section.title}
                  </Typography>
                </Box>
                {expandedSections[section.id] ? (
                  <ExpandLessIcon color="primary" />
                ) : (
                  <ExpandMoreIcon color="primary" />
                )}
              </SectionHeader>

              <Collapse
                in={expandedSections[section.id]}
                timeout="auto"
                unmountOnExit
              >
                <CardContent sx={{ pt: 3 }}>
                  {section.subsections.map((subsection) => (
                    <Box key={subsection.id}>
                      {renderSubsection(subsection)}
                    </Box>
                  ))}
                </CardContent>
              </Collapse>
            </SectionCard>
          </Grid>
        ))}

        {/* Notification Access Matrix (saved inside notification.roleMatrix) */}
        <Grid item xs={12}>
          {renderNotificationMatrix()}
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

export default SettingsPage;
